import {
  Add,
  Assessment,
  CheckCircle,
  Error,
  Settings,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import { ConfigurationValidation } from '../components/configuration/ConfigurationValidation';
import { FormulaConfigurationForm } from '../components/configuration/FormulaConfigurationForm';
import { MetricDefinitionForm } from '../components/configuration/MetricDefinitionForm';
import { WorkItemMappingForm } from '../components/configuration/WorkItemMappingForm';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { configurationApi } from '../services/configuration-api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const MetricsConfigurationPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState<
    'metric' | 'mapping' | 'formula' | null
  >(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // Check if user has admin permissions
  const isAdmin = user?.roles.includes('Leadership');

  // Fetch metric definitions
  const {
    data: metricDefinitions,
    isLoading: loadingMetrics,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: ['metricDefinitions'],
    queryFn: () => configurationApi.getMetricDefinitions(),
    enabled: isAdmin,
  });

  // Fetch work item mappings
  const {
    data: workItemMappings,
    isLoading: loadingMappings,
    refetch: refetchMappings,
  } = useQuery({
    queryKey: ['workItemMappings', user?.organizationId],
    queryFn: () =>
      configurationApi.getWorkItemMappings(user?.organizationId || ''),
    enabled: !!user?.organizationId,
  });

  // Fetch formula configurations
  const {
    data: formulaConfigs,
    isLoading: loadingFormulas,
    refetch: refetchFormulas,
  } = useQuery({
    queryKey: ['formulaConfigurations'],
    queryFn: () => configurationApi.getFormulaConfigurations(),
    enabled: isAdmin,
  });

  // Fetch validation results
  const {
    data: validationResults,
    isLoading: loadingValidation,
    refetch: refetchValidation,
  } = useQuery({
    queryKey: ['configurationValidation', user?.organizationId],
    queryFn: () =>
      configurationApi.validateConfiguration(user?.organizationId || ''),
    enabled: !!user?.organizationId,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (
    type: 'metric' | 'mapping' | 'formula',
    item?: any
  ) => {
    setSelectedItem(item || null);
    setOpenDialog(type);
  };

  const handleCloseDialog = () => {
    setOpenDialog(null);
    setSelectedItem(null);
  };

  const handleSaveSuccess = () => {
    handleCloseDialog();
    // Refetch data based on the dialog type
    switch (openDialog) {
      case 'metric':
        refetchMetrics();
        break;
      case 'mapping':
        refetchMappings();
        break;
      case 'formula':
        refetchFormulas();
        break;
    }
    refetchValidation();
  };

  const getStatusIcon = (isValid: boolean, hasWarnings: boolean) => {
    if (!isValid) return <Error color='error' />;
    if (hasWarnings) return <Warning color='warning' />;
    return <CheckCircle color='success' />;
  };

  const getStatusColor = (isValid: boolean, hasWarnings: boolean) => {
    if (!isValid) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  if (!isAdmin && !user?.roles.includes('ScrumMaster')) {
    return (
      <Box>
        <Alert severity='warning'>
          You don't have permission to access metric configuration.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Settings sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant='h4' component='h1'>
          Metrics Configuration
        </Typography>
      </Box>

      <Typography variant='body1' color='text.secondary' paragraph>
        Configure metric definitions, work item type mappings, and calculation
        formulas
      </Typography>

      {/* Configuration Status Overview */}
      {validationResults && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Assessment sx={{ mr: 1 }} />
            <Typography variant='h6'>Configuration Status</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {getStatusIcon(
                      validationResults.isValid,
                      validationResults.warnings.length > 0
                    )}
                    <Typography variant='h6' sx={{ ml: 1 }}>
                      Overall Status
                    </Typography>
                  </Box>
                  <Chip
                    label={validationResults.isValid ? 'Valid' : 'Invalid'}
                    color={getStatusColor(
                      validationResults.isValid,
                      validationResults.warnings.length > 0
                    )}
                    size='small'
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant='h6' color='error'>
                    {validationResults.errors.length}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Errors
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant='h6' color='warning.main'>
                    {validationResults.warnings.length}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Warnings
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Configuration Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label='configuration tabs'
          variant='scrollable'
          scrollButtons='auto'
        >
          <Tab label='Metric Definitions' />
          <Tab label='Work Item Mappings' />
          {isAdmin && <Tab label='Formula Configuration' />}
          <Tab label='Validation Results' />
        </Tabs>

        {/* Metric Definitions Tab */}
        <TabPanel value={activeTab} index={0}>
          {loadingMetrics ? (
            <LoadingSpinner />
          ) : (
            <Box>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
              >
                <Typography variant='h6'>Metric Definitions</Typography>
                {isAdmin && (
                  <Button
                    variant='contained'
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog('metric')}
                  >
                    Add Metric
                  </Button>
                )}
              </Box>
              <Grid container spacing={2}>
                {metricDefinitions?.map((metric: any) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={metric.id}>
                    <Card>
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant='h6' noWrap>
                            {metric.name}
                          </Typography>
                          <Chip
                            label={metric.category}
                            size='small'
                            color='primary'
                            variant='outlined'
                          />
                        </Box>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          paragraph
                        >
                          {metric.description}
                        </Typography>
                        <Typography
                          variant='caption'
                          display='block'
                          gutterBottom
                        >
                          Formula: {metric.formula}
                        </Typography>
                        <Typography
                          variant='caption'
                          display='block'
                          gutterBottom
                        >
                          Unit: {metric.unit}
                        </Typography>
                        {isAdmin && (
                          <Box sx={{ mt: 2 }}>
                            <Button
                              size='small'
                              onClick={() => handleOpenDialog('metric', metric)}
                            >
                              Edit
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </TabPanel>

        {/* Work Item Mappings Tab */}
        <TabPanel value={activeTab} index={1}>
          {loadingMappings ? (
            <LoadingSpinner />
          ) : (
            <Box>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
              >
                <Typography variant='h6'>Work Item Type Mappings</Typography>
                <Button
                  variant='contained'
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog('mapping')}
                >
                  Add Mapping
                </Button>
              </Box>
              <Grid container spacing={2}>
                {workItemMappings?.map((mapping: any) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={mapping.id}>
                    <Card>
                      <CardContent>
                        <Typography variant='h6' gutterBottom>
                          {mapping.sourceType}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          paragraph
                        >
                          Maps to: {mapping.targetCategory}
                        </Typography>
                        {mapping.effortEstimation && (
                          <Typography variant='caption' display='block'>
                            Default Size: {mapping.effortEstimation.defaultSize}
                          </Typography>
                        )}
                        {mapping.businessValue && (
                          <Typography variant='caption' display='block'>
                            Business Value: {mapping.businessValue}
                          </Typography>
                        )}
                        <Box sx={{ mt: 2 }}>
                          <Button
                            size='small'
                            onClick={() => handleOpenDialog('mapping', mapping)}
                          >
                            Edit
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </TabPanel>

        {/* Formula Configuration Tab */}
        {isAdmin && (
          <TabPanel value={activeTab} index={2}>
            {loadingFormulas ? (
              <LoadingSpinner />
            ) : (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant='h6'>Formula Configurations</Typography>
                  <Button
                    variant='contained'
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog('formula')}
                  >
                    Add Formula
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {formulaConfigs?.map((formula: any) => (
                    <Grid size={{ xs: 12, md: 6 }} key={formula.id}>
                      <Card>
                        <CardContent>
                          <Typography variant='h6' gutterBottom>
                            {formula.metricId}
                          </Typography>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            paragraph
                          >
                            {formula.formula}
                          </Typography>
                          <Typography variant='caption' display='block'>
                            Test Cases: {formula.testCases.length}
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <Button
                              size='small'
                              onClick={() =>
                                handleOpenDialog('formula', formula)
                              }
                            >
                              Edit
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </TabPanel>
        )}

        {/* Validation Results Tab */}
        <TabPanel value={activeTab} index={isAdmin ? 3 : 2}>
          {loadingValidation ? (
            <LoadingSpinner />
          ) : (
            <ConfigurationValidation
              validationResults={validationResults}
              onRefresh={refetchValidation}
            />
          )}
        </TabPanel>
      </Paper>

      {/* Dialogs */}
      <Dialog
        open={openDialog === 'metric'}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {selectedItem ? 'Edit Metric Definition' : 'Add Metric Definition'}
        </DialogTitle>
        <DialogContent>
          <MetricDefinitionForm
            initialData={selectedItem}
            onSave={handleSaveSuccess}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'mapping'}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          {selectedItem ? 'Edit Work Item Mapping' : 'Add Work Item Mapping'}
        </DialogTitle>
        <DialogContent>
          <WorkItemMappingForm
            initialData={selectedItem}
            organizationId={user?.organizationId || ''}
            onSave={handleSaveSuccess}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'formula'}
        onClose={handleCloseDialog}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          {selectedItem
            ? 'Edit Formula Configuration'
            : 'Add Formula Configuration'}
        </DialogTitle>
        <DialogContent>
          <FormulaConfigurationForm
            initialData={selectedItem}
            metricDefinitions={metricDefinitions || []}
            onSave={handleSaveSuccess}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MetricsConfigurationPage;
